import { useCallback, useEffect } from 'react'
import { reaction } from 'mobx'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { ErrorBoundary } from '@sentry/react'
import i18n from 'i18next'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react-lite'
import css from 'classnames'
import { MdOutlineConfirmationNumber } from 'react-icons/md'
import {
  canHandleBaseLayoutRealtimeEvent,
  getBaseLayoutBackgroundAccess,
  ORDER_STATUS_COUNTS_UPDATED_EVENT,
  SHIPPING_ORDERS_UPDATED_EVENT,
} from './lib/background-access'
import { subscribeTicketUnreadInvalidations } from '~/entities/tickets/lib/unread-sync'
import { useStore } from '~/app/store'
import { fallbackRender } from '~/pages/fallback-render'
import { Header } from '~/widgets'
import { useNavType, useToast } from '~/shared/lib'
import { SideBar } from '~/widgets/sidebar'
import {
  isOrdersData,
  isOrderStatusCounts,
  isTicketMessageCreatedData,
  isTicketUpdateNotificationData,
} from './lib/realtime-payload-guards'
import { useTicketUnreadFavicon } from './lib/use-ticket-unread-favicon'

const TICKET_MESSAGE_CREATED_EVENT = 'ticket.message.created'
const TICKET_NOTIFICATION_CREATED_EVENT = 'ticket.notification.created'

export const BaseLayout = observer(() => {
  const location = useLocation()
  const navigate = useNavigate()
  const [navType] = useNavType()
  const { t } = useTranslation('tickets')
  const toast = useToast()

  const {
    usersStore,
    printerStore,
    warehousesStore,
    realtimeStore,
    deliveryStore,
    ticketsStore,
    globalStore,
  } = useStore()
  const { getProfile } = usersStore
  const { setPrintersProfile } = printerStore
  const { fetchOrderStatusCounts, setOrderStatusCounts } = warehousesStore
  const currentUserId = usersStore.userProfile?.id
  const backgroundAccess = getBaseLayoutBackgroundAccess(usersStore.userProfile)
  const { canGetOrderList, canGetShippingList, canGetStoreList } =
    backgroundAccess
  const isTicketsRoute =
    location.pathname === '/tickets' ||
    location.pathname.startsWith('/tickets/')

  useTicketUnreadFavicon(isTicketsRoute && ticketsStore.hasUnreadUpdates)

  const syncOrderStatusCounts = useCallback(() => {
    if (!canGetOrderList) return

    fetchOrderStatusCounts()
      .then(() => {
        if (!canGetShippingList) {
          warehousesStore.setOrderStatusCount('dispatch', 0)
        }
      })
      .catch(() => undefined)
  }, [
    canGetOrderList,
    canGetShippingList,
    fetchOrderStatusCounts,
    warehousesStore,
  ])

  const syncTicketUnreadUpdates = useCallback(() => {
    ticketsStore.syncUnreadUpdates().catch(() => undefined)
    ticketsStore
      .refreshLoadedTickets({ silentForbidden: true })
      .catch(() => undefined)
  }, [ticketsStore])

  useEffect(() => {
    const fetchData = async () => {
      const data = await getProfile()
      setPrintersProfile(data.settings.printers)

      const profileLanguage = data.settings?.localization?.toLowerCase()
      if (
        profileLanguage &&
        profileLanguage !== i18n.resolvedLanguage?.toLowerCase()
      ) {
        i18n.changeLanguage(profileLanguage)
      }
    }

    fetchData()
  }, [getProfile, setPrintersProfile])

  useEffect(() => {
    if (!usersStore.userProfile) return

    globalStore.resetStoresForbiddenContext()

    const legalEntityId = usersStore.userProfile.legalEntity?.id
    const hasCachedEntityStores = globalStore.stores.some(
      (store) => store.legalEntityId === legalEntityId,
    )

    if (canGetStoreList) {
      if (legalEntityId && !hasCachedEntityStores && !globalStore.loading) {
        globalStore.getStores(legalEntityId).catch(() => undefined)
      }
    } else {
      globalStore.clearStores()
    }
  }, [canGetStoreList, globalStore, usersStore.userProfile])

  useEffect(() => {
    if (canGetOrderList) syncOrderStatusCounts()
  }, [canGetOrderList, currentUserId, syncOrderStatusCounts])

  useEffect(() => {
    if (!usersStore.userProfile) return

    if (!canGetOrderList) {
      warehousesStore.setOrderStatusCount('assembly', 0)
      warehousesStore.setOrderStatusCount('express', 0)
    }

    if (!canGetShippingList) {
      deliveryStore.setShippingOrdersFromPayload()
      warehousesStore.setOrderStatusCount('dispatch', 0)
    }
  }, [
    canGetOrderList,
    canGetShippingList,
    deliveryStore,
    usersStore.userProfile,
    warehousesStore,
  ])

  useEffect(() => {
    syncTicketUnreadUpdates()

    return () => {
      ticketsStore.resetUnreadUpdates()
    }
  }, [currentUserId, syncTicketUnreadUpdates, ticketsStore])

  useEffect(() => {
    if (!currentUserId) {
      ticketsStore.resetUnreadUpdates()
      return
    }

    return subscribeTicketUnreadInvalidations(currentUserId, () => {
      ticketsStore.syncUnreadUpdates().catch(() => undefined)
      ticketsStore
        .refreshLoadedTickets({ silentForbidden: true })
        .catch(() => undefined)
    })
  }, [currentUserId, ticketsStore])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (/^\/tickets\/[^/]+$/.test(location.pathname)) {
          ticketsStore
            .refreshLoadedTickets({ silentForbidden: true })
            .catch(() => undefined)
        } else {
          syncTicketUnreadUpdates()
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [location.pathname, syncTicketUnreadUpdates, ticketsStore])

  useEffect(() => {
    const disposeRealtime = reaction(
      () => realtimeStore.lastEvent,
      (event) => {
        if (
          !canHandleBaseLayoutRealtimeEvent(
            event?.event,
            usersStore.userProfile,
          )
        ) {
          return
        }

        if (event?.event === TICKET_MESSAGE_CREATED_EVENT) {
          if (!isTicketMessageCreatedData(event.data)) {
            return
          }

          const { ticketId } = event.data
          const isCurrentTicket = location.pathname === `/tickets/${ticketId}`
          ticketsStore
            .refreshLoadedTickets({ silentForbidden: true })
            .catch(() => undefined)

          if (isCurrentTicket) {
            ticketsStore
              .getTicketById(ticketId, { silentForbidden: true })
              .catch(() => undefined)
          }
          return
        }

        if (event?.event === TICKET_NOTIFICATION_CREATED_EVENT) {
          if (!isTicketUpdateNotificationData(event.data)) {
            return
          }

          if (
            !ticketsStore.registerRealtimeUpdate(
              event.data.notificationId,
              event.data.ticketId,
              event.data.hasUnreadUpdates,
            )
          ) {
            return
          }

          const { ticketId, ticketNumber, type } = event.data
          ticketsStore
            .refreshLoadedTickets({ silentForbidden: true })
            .catch(() => undefined)
          if (location.pathname === `/tickets/${ticketId}`) {
            const refreshCurrentTicket = ticketsStore.getTicketById(ticketId, {
              silentForbidden: true,
            })

            if (document.visibilityState === 'visible') {
              refreshCurrentTicket
                .then(() =>
                  ticketsStore.markUnreadUpdatesRead(
                    ticketId,
                    usersStore.userProfile?.id,
                    { silentForbidden: true },
                  ),
                )
                .catch(() => undefined)
            } else {
              refreshCurrentTicket.catch(() => undefined)
            }
          }
          toast.open(
            (close) => (
              <div className="space-y-2">
                <p className="font-semibold leading-5 text-slate-900">
                  {type === 'message_added'
                    ? t('replyNotification', {
                        number: ticketNumber ?? '—',
                      })
                    : t('inProgressNotification', {
                        number: ticketNumber ?? '—',
                      })}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    close()
                    navigate(`/tickets/${ticketId}`)
                  }}
                  className="rounded text-sm font-semibold text-blue-700 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                >
                  {t('openTicket')}
                </button>
              </div>
            ),
            'primary',
            {
              replaceKey: 'ticket-update',
              autoClose: false,
              closeLabel: t('dismissNotification'),
              className:
                '!mb-0 w-[25rem] max-w-full !items-start !rounded-xl border border-blue-100 !bg-white !shadow-lg',
              icon: (
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                  <MdOutlineConfirmationNumber size={22} aria-hidden="true" />
                </span>
              ),
            },
          )
          return
        }

        if (event?.event !== ORDER_STATUS_COUNTS_UPDATED_EVENT) {
          if (event?.event !== SHIPPING_ORDERS_UPDATED_EVENT) {
            return
          }

          if (!isOrdersData(event.data)) {
            return
          }

          deliveryStore.setShippingOrdersFromPayload(event.data)
          setOrderStatusCounts({
            ...warehousesStore.orderStatusCounts,
            dispatch: deliveryStore.getShippingOrdersCount(
              event.data.ordersData.dates,
            ),
          })
          return
        }

        if (isOrderStatusCounts(event.data)) {
          setOrderStatusCounts({
            ...event.data,
            ...(!canGetShippingList ? { dispatch: 0 } : {}),
          })
        }
      },
    )

    const disposeLegalEntity = reaction(
      () => usersStore.userProfile?.legalEntity?.id,
      (legalEntityId, previousLegalEntityId) => {
        if (legalEntityId && legalEntityId !== previousLegalEntityId) {
          if (canGetOrderList) syncOrderStatusCounts()

          const hasCachedEntityStores = globalStore.stores.some(
            (store) => store.legalEntityId === legalEntityId,
          )

          if (
            previousLegalEntityId &&
            !hasCachedEntityStores &&
            !globalStore.loading
          ) {
            if (canGetStoreList) {
              globalStore.getStores(legalEntityId).catch(() => undefined)
            }
          }
        }
      },
    )

    const disposeRealtimeConnection = reaction(
      () => realtimeStore.connectionState,
      (connectionState, previousConnectionState) => {
        if (
          connectionState === 'connected' &&
          previousConnectionState !== 'connected'
        ) {
          syncTicketUnreadUpdates()
        }
      },
    )

    return () => {
      disposeRealtime()
      disposeLegalEntity()
      disposeRealtimeConnection()
    }
  }, [
    deliveryStore,
    globalStore,
    canGetOrderList,
    canGetShippingList,
    canGetStoreList,
    location.pathname,
    navigate,
    realtimeStore,
    setOrderStatusCounts,
    syncOrderStatusCounts,
    syncTicketUnreadUpdates,
    t,
    ticketsStore,
    toast,
    usersStore,
    warehousesStore,
  ])

  const nav = navType === 'horizontal' ? <Header /> : <SideBar />

  return (
    <div
      className={`
        min-h-screen h-auto bg-[#F0F0F0] text-gray-700
        ${css({ 'flex flex-col md:flex-row': navType === 'vertical' })}
      `}
    >
      {nav}
      <ErrorBoundary fallback={fallbackRender} key={location.pathname}>
        <div
          className={`w-full ${
            location.pathname !== '/' && 'py-3 px-6'
          } min-w-0`}
        >
          <Outlet />
        </div>
      </ErrorBoundary>
    </div>
  )
})
